import {
  PROGRAM_TASK_CRITERIA_TYPES,
  isAccountStatusId,
  isProgramId,
  isRoleId,
  isTeamId,
  isWorkflowActionId,
  isWorkflowStageId,
  type ProgramAutoEnrollmentTrigger,
  type ProgramData,
  type ProgramTaskCriteria,
  type ProgramTaskCriteriaType,
  type ProgramTaskData,
  type ProgramTaskSchedule,
  type ProgramTrigger,
  type ProgramUserData,
  type ProgramUserStatus,
  type TaskAction,
  type TaskId,
  type TinyProgram,
  type VideoId,
} from '../model'
import * as Linking from '../linking'
import { unique } from './array'
import { isDefined, isNotDefined, isNotEmpty } from './isEmpty'

export function isProgramTaskCriteriaType(val: unknown): val is ProgramTaskCriteriaType {
  if (typeof val !== 'string') return false
  return PROGRAM_TASK_CRITERIA_TYPES.some((e) => val === e)
}

export function toProgramAutoEnrollmentTrigger(
  trigger: ProgramTrigger | null | undefined,
): ProgramAutoEnrollmentTrigger | undefined {
  if (trigger === 'userCreated') return { type: 'userCreated' }
  if (isAccountStatusId(trigger) || isWorkflowStageId(trigger)) return { type: 'stage', stageId: trigger }
  if (isWorkflowActionId(trigger)) return { type: 'action', actionId: trigger }
  if (isRoleId(trigger)) return { type: 'role', roleId: trigger }
  if (isProgramId(trigger)) return { type: 'program', programId: trigger }
  if (isTeamId(trigger)) return { type: 'team', teamId: trigger }
}

export function toProgramTrigger(trigger: ProgramAutoEnrollmentTrigger): ProgramTrigger {
  switch (trigger.type) {
    case 'stage':
      return trigger.stageId
    case 'action':
      return trigger.actionId
    case 'role':
      return trigger.roleId
    case 'team':
      return trigger.teamId
    case 'program':
      return trigger.programId
    case 'userCreated':
      return 'userCreated'
  }
}

export function getProgramAutoEnrollmentTriggers(
  program: Pick<ProgramData, 'trigger' | 'triggers'>,
): ProgramAutoEnrollmentTrigger[] {
  if (program.triggers) return program.triggers
  const trigger = toProgramAutoEnrollmentTrigger(program.trigger)
  return trigger ? [trigger] : []
}

export function programHasTrigger(program: Pick<ProgramData, 'trigger' | 'triggers'>, trigger: ProgramTrigger): boolean {
  return getProgramAutoEnrollmentTriggers(program).some((programTrigger) => toProgramTrigger(programTrigger) === trigger)
}

export function getProgramWeekCount(program: Pick<ProgramData, 'totalDays'>): number {
  if (!program.totalDays) return 0
  return Math.ceil(program.totalDays / 7)
}

export function getTotalTaskOccurances(schedule: ProgramTaskSchedule, totalDays: number | undefined): number {
  if (schedule.type === 'day') return schedule.days.length
  if (schedule.type === 'weekday') return schedule.days.reduce((acc, day) => acc + day.weeks.length, 0)
  if (schedule.type === 'sequentialWeekday' && isDefined(totalDays))
    return getProgramWeekCount({ totalDays }) * schedule.weekdays.length
  return 0
}

export function filterActiveProgramUsers<T extends Pick<ProgramUserData, 'status'>>(programUsers: T[]): T[] {
  return programUsers.filter((pu) => pu.status === 'active')
}

export function areAllTasksWithinProgramLimits(
  { completion, totalDays }: Pick<ProgramData, 'completion' | 'totalDays'>,
  tasks: Pick<ProgramTaskData, 'schedule'>[]
): boolean {
  if (isNotDefined(totalDays)) return true
  if (completion === 'taskCompletion') return true
  return tasks.every((task): boolean => {
    switch (task.schedule.type) {
      case 'day':
        return task.schedule.days.every((d) => 0 < d && d <= totalDays)
      case 'weekday':
        return task.schedule.days.every((d) => d.weeks.every((w) => 0 < w && w <= getProgramWeekCount({ totalDays })))
      case 'sequentialWeekday':
      case 'none':
        return true
    }
  })
}

export function toOptionalTinyProgram(program?: ProgramData): TinyProgram | undefined {
  if (!program) return
  return toTinyProgram(program)
}

export function toTinyProgram(program: ProgramData): TinyProgram {
  return {
    programId: program.programId,
    name: program.name,
    ownerId: program.ownerId,
    teamIds: program.teamIds,
    totalDays: program.totalDays,
    completion: program.completion,
  }
}

export function sortProgramUsersByStartDate(
  a: Pick<ProgramUserData, 'startDate'>,
  b: Pick<ProgramUserData, 'startDate'>
): number {
  return b.startDate - a.startDate
}

const ENDED_PROGRAM_USER_STATUSES: ProgramUserStatus[] = ['completed', 'dropped']

export function isProgramEnrollmentEnded(programUser: Pick<ProgramUserData, 'status'> | undefined): boolean {
  if (isNotDefined(programUser)) return false
  return ENDED_PROGRAM_USER_STATUSES.includes(programUser.status)
}

export function getTaskBody(
  programTask: Pick<ProgramTaskData, 'body' | 'subject'>,
  criteria: ProgramTaskCriteria | undefined
): string {
  if (!criteria) return programTask.body
  switch (criteria.type) {
    case 'video':
    case 'videoChat': {
      const description = normalizeTaskText(criteria.description)
      if (!description || description === normalizeTaskText(programTask.subject)) return programTask.body
      return criteria.description ?? programTask.body
    }
    default:
      return programTask.body
  }
}

const normalizeTaskText = (text: string | undefined): string => text?.trim().replace(/\s+/g, ' ').toLowerCase() ?? ''

export function getActionsFromCriteria(criteria: ProgramTaskCriteria | undefined, taskId: TaskId): TaskAction[] {
  switch (criteria?.type) {
    case 'activity':
    case 'workflow':
      return [
        {
          label: `Go to Canvass`,
          link: Linking.toCanvass({ includeScheme: true }),
        },
      ]
    case 'evaluation':
      return [
        {
          label: 'Record Evaluation',
          link: Linking.toEvaluations({ includeScheme: true }),
        },
      ]
    case 'shadow':
      return [
        {
          label: `Schedule Shadow Day`,
          link: Linking.toTask({ taskId, includeScheme: true }),
        },
      ]
    case 'video':
      if (!criteria.videoId) return []
      return [
        {
          label: `Watch Video`,
          link: Linking.toTask({ taskId, includeScheme: true }),
        },
      ]
    case 'videoChat':
      return [
        {
          label: `Send Message`,
          link: Linking.toTask({ includeScheme: true, taskId }),
        },
      ]
    case 'externalLink':
      return [
        {
          label: isNotEmpty(criteria.label) ? criteria.label : 'Open Link',
          link: criteria.url,
        },
      ]
    case 'convo':
      return [
        {
          label: 'Record Conversation',
          link: Linking.toConversationList({ includeScheme: true }),
        },
      ]
    case 'quiz':
      return [
        {
          label: 'Take Quiz',
          link: Linking.toTakeQuiz({ quizId: criteria.quizId, includeScheme: true }),
        },
      ]
  }
  return []
}

export function sortPrograms<T extends Pick<ProgramData, 'name'>>(programs: T[]): T[] {
  return programs.sort((a, b) => a.name.localeCompare(b.name))
}

export function getProgramVideoIds(program: Pick<ProgramData, 'tasks'>): VideoId[] {
  return unique(
    program.tasks.flatMap((task) =>
      task.criteria.flatMap((criteria) => (criteria.type === 'video' && criteria.videoId ? [criteria.videoId] : []))
    )
  )
}
